import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import csJson from "./cs.json";


export const supportedLanguages = ["cs", "en", "de", "sk", "hr", "it", "nl", "et", "lt", "lv", "hu"] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];
type NonCzechLanguage = Exclude<SupportedLanguage, "cs">;

const langLoaders: Record<NonCzechLanguage, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import("./en.json"),
  de: () => import("./de.json"),
  sk: () => import("./sk.json"),
  hr: () => import("./hr.json"),
  it: () => import("./it.json"),
  nl: () => import("./nl.json"),
  et: () => import("./et.json"),
  lt: () => import("./lt.json"),
  lv: () => import("./lv.json"),
  hu: () => import("./hu.json"),
};


export function detectLanguageFromBrowser(): SupportedLanguage {
  const browserLang = navigator.language?.slice(0, 2).toLowerCase();
  if (supportedLanguages.includes(browserLang as SupportedLanguage)) {
    return browserLang as SupportedLanguage;
  }
  return "cs";
}


// Initialize i18n synchronously with only Czech to minimize startup blocking
// Other languages are loaded on-demand or in background
i18n.use(initReactI18next).init({
  resources: {
    cs: { translation: csJson },
  },
  lng: "cs",
  fallbackLng: "cs",
  interpolation: { escapeValue: false },
});

// Preload other languages in the background (non-blocking)
// This loads language bundles asynchronously without delaying app startup
export async function ensureLanguageResources(lang: SupportedLanguage) {
  if (lang === "cs") return;
  if (i18n.hasResourceBundle(lang, "translation")) return;

  const data = await langLoaders[lang]();
  i18n.addResourceBundle(lang, "translation", data.default || data, true, true);
}

export function preloadLanguages() {
  const langs = Object.keys(langLoaders) as NonCzechLanguage[];
  Promise.all(
    langs.map(async (lang) => {
      try {
        await ensureLanguageResources(lang);
      } catch (error) {
        console.warn(`Failed to load language: ${lang}`, error);
      }
    })
  ).catch((error) => console.warn("Failed to preload languages:", error));
}

// Setup i18n with supported languages metadata
export const languageLabels: Record<SupportedLanguage, string> = {
  cs: "Čeština",
  en: "English",
  de: "Deutsch",
  sk: "Slovenčina",
  hr: "Hrvatski",
  it: "Italiano",
  nl: "Nederlands",
  et: "Eesti",
  lt: "Lietuvių",
  lv: "Latviešu",
  hu: "Magyar",
};

export const languageFlags: Record<SupportedLanguage, string> = {
  cs: "🇨🇿",
  en: "🇬🇧",
  de: "🇩🇪",
  sk: "🇸🇰",
  hr: "🇭🇷",
  it: "🇮🇹",
  nl: "🇳🇱",
  et: "🇪🇪",
  lt: "🇱🇹",
  lv: "🇱🇻",
  hu: "🇭🇺",
};

export default i18n;
